import React from 'react';

/**
 * Centralised SVG icon library for the Taskydo SPA.
 *
 * Why SVG (not emoji)
 * -------------------
 * The original shell used colour emoji (📋 📨 💬 …) for the left-rail
 * tiles, the top-bar title, the mobile menu and the footer bar.  Emoji
 * are quick to ship but they:
 *
 *   * render *very* differently across macOS / Windows / Linux / Android
 *     fonts, so the same screenshot looks off-brand depending on which
 *     OS the user is on,
 *   * are filled glyphs that fight the otherwise outlined / minimalist
 *     UI language we use for buttons, status pills and form chrome,
 *   * can't be tinted via ``color`` (they're always full-colour
 *     bitmaps), so the active-state highlight on the rail tile is just
 *     a background swatch — the glyph itself never reacts,
 *   * have inconsistent optical weights at small sizes (the pill 💊 is
 *     wildly heavier than the envelope ✉️, etc.).
 *
 * Every icon below is a 24×24 outlined SVG drawn on the same grid with
 * the same ``stroke-width`` (1.7) and ``stroke-linecap="round"`` so the
 * row of tiles in the app rail reads as one coherent set.  They use
 * ``currentColor`` for both stroke and fill, so the existing CSS rules
 * on ``.ty-app-rail__btn`` and ``.ty-app-rail__btn--active`` tint them
 * for free.
 *
 * Each icon component accepts the standard SVG props (``size`` shorthand
 * for ``width`` / ``height``, plus anything the consumer wants to pass
 * through such as ``aria-hidden`` or ``className``).  The default size
 * is 22px which matches the legacy emoji rendering on the rail without
 * any CSS changes — see ``.ty-app-rail__icon`` in ``global.css``.
 */


/**
 * Internal helper — every Taskydo icon shares the same base props so
 * the whole set lines up on the rail without per-icon tweaking.  Pulled
 * into one place so a future style change (e.g. switching to a 20px
 * grid or a heavier stroke) is a one-line edit.
 */
function IconBase({ size = 22, children, title, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}


/** Tasks — clipboard with a tick.  Replaces 📋. */
export function TasksIcon(props) {
  return (
    <IconBase {...props}>
      <rect x="5" y="4" width="14" height="17" rx="2.2" />
      <path d="M9 4h6a1 1 0 0 1 1 1v1.5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
      <path d="m9.5 13.5 2 2 3.5-4" />
    </IconBase>
  );
}


/** Complaints — speech bubble with an alert dot.  Replaces 📨. */
export function ComplaintsIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v8.5a1.5 1.5 0 0 1-1.5 1.5h-8.6L7 20v-3H5a1.5 1.5 0 0 1-1.5-1.5V7A1.5 1.5 0 0 1 5 5.5Z" />
      <path d="M12 9.25v3" />
      <circle cx="12" cy="14.25" r="0.55" fill="currentColor" stroke="none" />
    </IconBase>
  );
}


/** Chat — overlapping conversation bubbles.  Replaces 💬. */
export function ChatIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M3.5 9.75A4.25 4.25 0 0 1 7.75 5.5h6.5A4.25 4.25 0 0 1 18.5 9.75v2.5a4.25 4.25 0 0 1-4.25 4.25H10l-3.25 2.5v-2.7a4.25 4.25 0 0 1-3.25-4.05Z" />
      <path d="M8 10.5h6M8 13h4" />
    </IconBase>
  );
}


/** Pharma SFA — capsule pill on a slight tilt.  Replaces 💊. */
export function PharmaIcon(props) {
  return (
    <IconBase {...props}>
      <rect
        x="3.5"
        y="9"
        width="17"
        height="6"
        rx="3"
        transform="rotate(-30 12 12)"
      />
      <path d="M9.4 7.85 14.6 16.15" />
    </IconBase>
  );
}


/** Email Marketing — envelope with a paper-plane swoop.  Replaces ✉️. */
export function EmailIcon(props) {
  return (
    <IconBase {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="m3.6 7 8.4 6.5L20.4 7" />
      <path d="M14.5 16.5 17 14l1 4Z" fill="currentColor" />
    </IconBase>
  );
}


/** SMS / DLT — phone with a chat bubble overlay.  Replaces 📱. */
export function SmsIcon(props) {
  return (
    <IconBase {...props}>
      <rect x="6" y="2.75" width="12" height="18.5" rx="2.5" />
      <path d="M10 19.25h4" />
      <path d="M9 6.5h7.5a.5.5 0 0 1 .5.5v3.5a.5.5 0 0 1-.5.5H13l-2 1.6V11H9a.5.5 0 0 1-.5-.5V7A.5.5 0 0 1 9 6.5Z" />
    </IconBase>
  );
}


/** Form Builder — sheet with form rows + a tick.  Replaces 📝. */
export function FormsIcon(props) {
  return (
    <IconBase {...props}>
      <rect x="4.5" y="3.5" width="15" height="17" rx="2" />
      <path d="M8 8h5" />
      <path d="M8 12h8" />
      <path d="m8 16.5 1.6 1.5L12.5 15" />
    </IconBase>
  );
}


/** WhatsApp Business — phone + chat bubble. */
export function WhatsAppIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M6.5 4.5h7A2.5 2.5 0 0 1 16 7v7.5A2.5 2.5 0 0 1 13.5 17H9l-3 2.2V17H6.5A2.5 2.5 0 0 1 4 14.5V7A2.5 2.5 0 0 1 6.5 4.5Z" />
      <path d="M17 9.5h.5A2.5 2.5 0 0 1 20 12v5.5A2.5 2.5 0 0 1 17.5 20H16l-1.6 1.3V20" />
    </IconBase>
  );
}


/** RAG — stacked knowledge pages with a spark. */
export function RagIcon(props) {
  return (
    <IconBase {...props}>
      <rect x="5" y="4.5" width="12" height="15" rx="1.8" />
      <path d="M8 8.5h6M8 12h6M8 15.5h3.5" />
      <path d="M16.5 6.5 18 4.8 19.5 6.5 18 8.2Z" fill="currentColor" stroke="none" />
    </IconBase>
  );
}


/** SignalBridge — Meta CAPI pixel / target. */
export function SignalBridgeIcon(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3.5v3.2M12 17.3v3.2M3.5 12h3.2M17.3 12h3.2" />
    </IconBase>
  );
}


/** Business — multi-storey office building.  Replaces 🏢. */
export function BusinessIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M4.5 20.5V6.5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v14" />
      <path d="M13.5 20.5V11h5a1 1 0 0 1 1 1v8.5" />
      <path d="M3.5 20.5h17" />
      <path d="M7 9h2M7 12.5h2M7 16h2" />
      <path d="M16 14.5h0.5M16 17.5h0.5" />
    </IconBase>
  );
}


/** Super Admin — shield with an inset checkmark.  Replaces 🛡️. */
export function SuperAdminIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M12 3 5 5.5v6.2c0 4.1 2.9 7.7 7 9.3 4.1-1.6 7-5.2 7-9.3V5.5L12 3Z" />
      <path d="m9 12 2.2 2.2L15 10.6" />
    </IconBase>
  );
}


/** Logout — power glyph.  Replaces ⏻ in the rail / mobile menu. */
export function LogoutIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M12 3.5v8" />
      <path d="M7.5 6.5a7 7 0 1 0 9 0" />
    </IconBase>
  );
}


/** Person — head + shoulders.  Used for the "My Profile" entry in the
 *  top-right user dropdown (see ``components/UserMenu.jsx``). */
export function UserIcon(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5" />
    </IconBase>
  );
}


/** Key — used for the "Change password" entry in the user dropdown. */
export function KeyIcon(props) {
  return (
    <IconBase {...props}>
      <circle cx="8" cy="16" r="3.5" />
      <path d="m10.5 13.5 7-7" />
      <path d="m15 6 3-3 3 3-3 3" />
    </IconBase>
  );
}


/** Chevron pointing down — the small caret on the user dropdown
 *  trigger that hints the name is clickable.  Rotates 180° via CSS when
 *  the menu is open. */
export function ChevronDownIcon(props) {
  return (
    <IconBase {...props}>
      <path d="m6 9.5 6 6 6-6" />
    </IconBase>
  );
}



/** Notification bell — top-bar "bell" trigger.  Pairs with the badge
 *  dot rendered by ``NotificationBell``. */
export function BellIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M6 9.5a6 6 0 0 1 12 0c0 3.3.8 5.2 1.7 6.3.5.6.1 1.7-.8 1.7H5.1c-.9 0-1.3-1.1-.8-1.7C5.2 14.7 6 12.8 6 9.5Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </IconBase>
  );
}


/** Warning — triangle with an exclamation.  Used for the "overdue /
 *  due-today / urgent" notification groups. */
export function WarningIcon(props) {
  return (
    <IconBase {...props}>
      <path d="M12 4.5 21 19.5H3L12 4.5Z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="16.75" r="0.55" fill="currentColor" stroke="none" />
    </IconBase>
  );
}


/** Chat (footer bar) — same set, slightly heavier outline at this size. */
export function ChatBubbleIcon(props) {

  return (
    <IconBase {...props}>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8A2.5 2.5 0 0 1 17.5 17H10l-4 3v-3a2.5 2.5 0 0 1-2-2.5Z" />
    </IconBase>
  );
}


/** Support — life-ring style help glyph.  Replaces ❓ in the footer. */
export function SupportIcon(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.5a2.5 2.5 0 1 1 3.4 2.4c-.7.3-1 .8-1 1.6V14" />
      <circle cx="12" cy="16.5" r="0.65" fill="currentColor" stroke="none" />
    </IconBase>
  );
}


/**
 * The Taskydo brand mark — a rounded indigo tile with a bold white
 * checkmark.  Used for the top of the app rail and reused (in raster
 * form) as the favicon.  Centralised here so a future rebrand is a
 * one-file edit.  Keep in sync with ``public/favicon.svg``.
 *
 * Renders an inline ``<svg>`` so it tints / scales crisply.  The
 * gradient id is suffixed with ``React.useId()`` to avoid clashes when
 * the mark is rendered more than once on the same page.
 */
export function BrandMark({ size = 36, rounded = 10, ...rest }) {
  const id = React.useId().replace(/[:]/g, '');
  const gradId = `ty-brand-${id}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label="Taskydo"
      focusable="false"
      {...rest}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#8896ff" />
          <stop offset="100%" stopColor="#4f55eb" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="48" height="48" rx={rounded * (48 / 36)} fill={`url(#${gradId})`} />
      <path
        d="M15.3 26.8 L22.0 31.5 L36.0 18.9"
        fill="none"
        stroke="#ffffff"
        strokeWidth="9.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


/**
 * Resolve a string ``key`` (the value stored on each ``NAV`` entry's
 * ``icon`` field in ``config.js``) to the corresponding component.
 *
 * Keeping the lookup table next to the components means a new app only
 * needs to (a) add a component above and (b) wire the key here — the
 * rest of the shell picks it up automatically.
 */
const REGISTRY = {
  tasks:        TasksIcon,
  complaints:   ComplaintsIcon,
  chat:         ChatIcon,
  pharma:       PharmaIcon,
  email:        EmailIcon,
  sms:          SmsIcon,
  forms:        FormsIcon,
  whatsapp:     WhatsAppIcon,
  rag:          RagIcon,
  signalbridge:    SignalBridgeIcon,
  business:     BusinessIcon,
  superAdmin:   SuperAdminIcon,
  logout:       LogoutIcon,
  chatBubble:   ChatBubbleIcon,
  support:      SupportIcon,
};

export function AppIcon({ name, ...rest }) {
  const Comp = REGISTRY[name];
  if (!Comp) {
    // Fail soft: render a small placeholder dot so the layout still
    // works even if a NAV entry references an unknown icon key.
    return (
      <IconBase {...rest}>
        <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
      </IconBase>
    );
  }
  return <Comp {...rest} />;
}


export default AppIcon;
